import axios from "axios";

/**
 * Upload an image to Cloudinary
 * @param base64Image - Base64 string of the image
 * @returns {Promise<string | null>} - Secure URL of the uploaded image
 */
export const uploadToCloudinary = async (
    base64Image: string
): Promise<string | null> => {
    console.log("Attempting to upload an image to Cloudinary...");

    const uniqueId =
        Date.now().toString(36) + Math.random().toString(36).substring(2);

    // Prepare the upload URL
    const uploadUrl = `https://api.cloudinary.com/v1_1/danbcslcx/image/upload`;

    try {
        // Prepare the form data
        const formData = new FormData();
        formData.append("file", base64Image);
        formData.append("upload_preset", "my-unsigned-preset");
        formData.append("public_id", uniqueId);
        formData.append("folder", "staging"); // Target folder: "staging"

        // Perform the upload
        const response = await axios.post(uploadUrl, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        if (response.status === 200 && response.data.secure_url) {
            console.log(
                "Image uploaded successfully:",
                response.data.secure_url
            );
            return response.data.secure_url;
        } else {
            console.error(
                "Unexpected response from Cloudinary:",
                response.data
            );
            return null;
        }
    } catch (error) {
        console.error("Error uploading image to Cloudinary:", error);
        return null;
    }
};

// Helper to inject Cloudinary transforms into an "upload" URL
// It inserts transform segments after `/image/upload/` and before the version/publicId.
// Example:
//   addCloudinaryTransforms(url, [
//     "f_auto,q_auto",
//     "c_crop,x_100,y_200,w_500,h_500",
//     "c_fill,w_256,h_256"
//   ])
export function addCloudinaryTransforms(
    originalUrl: string,
    segments: string[]
): string {
    if (!originalUrl) return originalUrl;
    const marker = "/image/upload/";
    const idx = originalUrl.indexOf(marker);
    if (idx === -1) return originalUrl; // Not a standard Cloudinary URL, bail.

    const before = originalUrl.slice(0, idx + marker.length);
    const after = originalUrl.slice(idx + marker.length); // usually like: v123/abc/def.jpg

    // If the URL already has transforms (rare in your staging flow), we still just prepend ours.
    const transforms = segments.filter(Boolean).join("/");

    return `${before}${transforms}/${after}`;
}

/** Given any Cloudinary URL (with or without transforms), return the original asset URL
 *  (keeps the /v123/ version + extension, strips transform chain).
 */
export function getCloudinaryOriginalUrl(anyUrl: string): string {
    try {
        const u = new URL(anyUrl);
        const p = u.pathname; // /<cloud>/image/upload/[transforms]/v123/folder/id.ext  OR  /.../v123/folder/id.ext
        const UPLOAD = "/upload/";
        const i = p.indexOf(UPLOAD);
        if (i === -1) return anyUrl;

        const before = p.slice(0, i + UPLOAD.length); // .../image/upload/
        const tail = p.slice(i + UPLOAD.length);

        // Prefer exact "/v123/..." tail if present
        const m = tail.match(/v\d+\/[^.]+\.[a-zA-Z0-9]+$/);
        if (m) {
            u.pathname = before + m[0];
            return u.toString();
        }

        // Fallback: no version (rare) — grab last segment (filename.ext)
        const lastSlash = tail.lastIndexOf("/");
        const filename = lastSlash >= 0 ? tail.slice(lastSlash + 1) : tail;
        u.pathname = before + filename;
        return u.toString();
    } catch {
        return anyUrl;
    }
}
